import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Building2, CheckCircle, Clock, RefreshCw, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_protected/admin/manual-sync')({
  component: ManualSyncPage,
});

// Form validation schema
const syncFormSchema = z.object({
  date: z.date(),
  facilityEhrId: z.string().min(1, 'Please select a facility'),
  providerEhrIds: z
    .array(z.string())
    .min(1, 'Please select at least 1 provider')
    .max(5, 'Maximum 5 providers allowed'),
});

type SyncFormData = z.infer<typeof syncFormSchema>;

// Multi-select component for providers
interface MultiSelectProps {
  options: Array<{ ehrId: string; name: string; id: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  maxSelection: number;
  placeholder?: string;
  disabled?: boolean;
}

function MultiSelect({
  options,
  value,
  onChange,
  maxSelection,
  placeholder,
  disabled,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (selectedEhrId: string) => {
    if (value.includes(selectedEhrId)) {
      // Remove if already selected
      onChange(value.filter((id) => id !== selectedEhrId));
    } else if (value.length < maxSelection) {
      // Add if under limit
      onChange([...value, selectedEhrId]);
    } else {
      toast.error(`Maximum ${maxSelection} providers allowed`);
    }
  };

  const selectedNames = options
    .filter((option) => value.includes(option.ehrId))
    .map((option) => option.name);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="truncate">
          {selectedNames.length > 0
            ? `${selectedNames.length} provider${selectedNames.length > 1 ? 's' : ''} selected`
            : placeholder || 'Select providers'}
        </span>
        <span className="ml-2 text-xs text-muted-foreground">
          {value.length}/{maxSelection}
        </span>
      </Button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            aria-label="Close dropdown"
          />
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            <div className="max-h-60 overflow-auto">
              {options.length === 0 ? (
                <div className="px-2 py-1 text-sm text-muted-foreground">
                  No providers available
                </div>
              ) : (
                options.map((option) => {
                  const isSelected = value.includes(option.ehrId);
                  return (
                    <button
                      key={option.ehrId}
                      type="button"
                      className={cn(
                        'flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent'
                      )}
                      onClick={() => handleSelect(option.ehrId)}
                    >
                      <span className="mr-2">{isSelected ? '✓' : ' '}</span>
                      {option.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ManualSyncPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization } = useOrganization();
  const router = useRouter();
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const currentOrganizationId = organization?.id;

  const form = useForm<SyncFormData>({
    resolver: zodResolver(syncFormSchema),
    defaultValues: {
      date: undefined,
      facilityEhrId: '',
      providerEhrIds: [],
    },
  });

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Cooldown timer effect
  useEffect(() => {
    if (lastSyncTime) {
      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastSyncTime;
        const remaining = Math.max(0, 60000 - elapsed); // 1 minute cooldown
        setCooldownRemaining(remaining);

        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastSyncTime]);

  // tRPC queries
  const facilitiesQuery = trpc.admin.getManualSyncFacilities.useQuery(
    {
      organizationId: currentOrganizationId || '',
      ehrSource: 'imedicware',
    },
    {
      enabled: !!currentOrganizationId && isSuperAdmin,
    }
  );

  const providersQuery = trpc.admin.getManualSyncProviders.useQuery(
    {
      organizationId: currentOrganizationId || '',
      ehrSource: 'imedicware',
    },
    {
      enabled: !!currentOrganizationId && isSuperAdmin,
    }
  );

  // Sync mutation
  const syncMutation = trpc.admin.triggerManualSync.useMutation({
    onSuccess: (data) => {
      toast.success('Sync completed successfully', {
        description: `Processed ${data.totalSlotsProcessed} slots for ${data.facilityEhrId}`,
      });
      setLastSyncTime(Date.now());
    },
    onError: (error) => {
      toast.error('Sync failed', {
        description: error.message,
      });
    },
  });

  const onSubmit = (data: SyncFormData) => {
    if (!currentOrganizationId) {
      toast.error('No organization selected');
      return;
    }

    // Check cooldown
    if (cooldownRemaining > 0) {
      toast.error(`Please wait ${Math.ceil(cooldownRemaining / 1000)} seconds before next sync`);
      return;
    }

    // Format date to YYYY-MM-DD using local timezone
    const dateStr = data.date
      ? `${data.date.getFullYear()}-${String(data.date.getMonth() + 1).padStart(2, '0')}-${String(data.date.getDate()).padStart(2, '0')}`
      : undefined;

    if (!dateStr) {
      toast.error('Please select a date');
      return;
    }

    syncMutation.mutate({
      organizationId: currentOrganizationId,
      ehrSource: 'imedicware',
      date: dateStr,
      facilityEhrId: data.facilityEhrId,
      providerEhrIds: data.providerEhrIds,
    });
  };

  const isLoading = facilitiesQuery.isLoading || providersQuery.isLoading;
  const isSyncing = syncMutation.isPending;
  const canSync = !isSyncing && cooldownRemaining === 0;

  if (isAdminLoading || !isSuperAdmin) {
    return null;
  }

  if (!currentOrganizationId) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">No Organization Selected</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please select an organization from the organization selector to use this tool.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Manual Appointment Sync"
      description="Trigger appointment sync for a specific facility and date"
      icon={RefreshCw}
      isSuperAdminPage={true}
    >
      {/* Stats Cards */}
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available Facilities</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{facilitiesQuery.data?.length || 0}</div>
              <p className="text-xs text-muted-foreground">Sync-enabled facilities</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available Providers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{providersQuery.data?.length || 0}</div>
              <p className="text-xs text-muted-foreground">Schedulable providers</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sync Status</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {cooldownRemaining > 0 ? (
                  <span className="text-orange-600">{Math.ceil(cooldownRemaining / 1000)}s</span>
                ) : (
                  <span className="text-green-600">Ready</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {cooldownRemaining > 0 ? 'Cooldown remaining' : 'Ready to sync'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Form */}
        <Card className="border-border/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Sync Configuration</CardTitle>
            <CardDescription>
              Select a facility, date, and optionally up to 5 providers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Date Selection */}
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sync Date</FormLabel>
                        <FormControl>
                          <DatePicker
                            date={field.value}
                            onDateChange={field.onChange}
                            placeholder="Select sync date"
                            disabled={isSyncing}
                            disablePast={true}
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Facility Selection */}
                  <FormField
                    control={form.control}
                    name="facilityEhrId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Facility</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          disabled={isLoading || isSyncing}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a facility" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {!facilitiesQuery.data || facilitiesQuery.data.length === 0 ? (
                              <div className="px-2 py-1 text-sm text-muted-foreground">
                                No facilities available
                              </div>
                            ) : (
                              facilitiesQuery.data.map((facility) => (
                                <SelectItem key={facility.ehrId} value={facility.ehrId}>
                                  {facility.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Provider Multi-Select */}
                <div className="sm:col-span-2">
                  <FormField
                    control={form.control}
                    name="providerEhrIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Providers</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={providersQuery.data || []}
                            value={field.value}
                            onChange={field.onChange}
                            maxSelection={5}
                            placeholder="Select 1 to 5 providers (required)"
                            disabled={isLoading || isSyncing}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground mt-1">
                          Select 1 to 5 providers to sync their appointments
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex flex-col gap-3 pt-2 sm:col-span-2 sm:flex-row sm:items-center">
                  <Button
                    type="submit"
                    disabled={!canSync || isLoading}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    {isSyncing ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Trigger Sync
                      </>
                    )}
                  </Button>

                  {cooldownRemaining > 0 && (
                    <div className="flex items-center justify-center gap-1.5 rounded-md bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 dark:bg-orange-950 dark:text-orange-300 sm:justify-start">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Cooldown: {Math.ceil(cooldownRemaining / 1000)}s</span>
                    </div>
                  )}
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-border/50">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">How to Use</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  1
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Select Date:</span> Choose the
                  specific date you want to sync appointments for
                </p>
              </div>
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  2
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Choose Facility:</span> Select
                  exactly one facility from the dropdown
                </p>
              </div>
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  3
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Pick Providers:</span> Select 1 to 5
                  providers to sync (required)
                </p>
              </div>
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  4
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Trigger Sync:</span> Click the
                  button to start the sync process
                </p>
              </div>
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  5
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Wait for Cooldown:</span> After
                  syncing, wait 1 minute before the next sync
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
              <p className="text-xs leading-relaxed text-yellow-800 dark:text-yellow-200">
                <strong>⚠️ Important:</strong> This tool directly modifies appointment data in the
                production database. Use with caution and verify results after syncing.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageLayout>
  );
}
